// PackagesPage: List of packages
import React, { useEffect, useState, useContext } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, List, ListItem, ListItemIcon, ListItemText, Chip, Button, Avatar, Stack, IconButton, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { ThemeContext } from '../context/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { getCMS } from '../services/cms';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getPackages } from '../services/api';
import BookingModal from '../components/BookingModal';


function formatPrice(price) {
  if (typeof price === 'number') return '$' + price.toLocaleString();
  if (typeof price === 'string' && price[0] !== '$') return '$' + price;
  return price;
}


function PackagesPage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [expandedDesc, setExpandedDesc] = useState({});
  const [showAllIncludes, setShowAllIncludes] = useState({});
  const theme = useContext(ThemeContext)?.theme || 'light';
  const muiTheme = useMuiTheme();
  const [bgMap, setBgMap] = useState({});

  useEffect(() => {
    getPackages()
      .then(data => {
        if (Array.isArray(data)) setPackages(data);
        else if (data && Array.isArray(data.packages)) setPackages(data.packages);
        else setPackages([]);
        setError("");
      })
      .catch(() => setError("Failed to load packages."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let mounted = true;
    getCMS().then(items => {
      if (!mounted) return;
      const landing = (items || []).find(i => i.type === 'landing');
      const map = landing?.contentJSON?.backgrounds || {};
      setBgMap(map || {});
    }).catch(() => { }).finally(() => { mounted = false; });
  }, []);

  return (
    <PublicLayout>
      <Box sx={{ width: '100%', mx: 'auto', mt: 1, px: { xs: 0.5, sm: 2, md: 3 }, position: 'relative', mb: 4 }}>
        {bgMap.packages && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgMap.packages})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', zIndex: 0 }} />
        )}
        {bgMap.packages && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: muiTheme.palette.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.12)', zIndex: 0 }} />
        )}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" sx={{ color: theme === 'dark' ? '#fff' : '#222', letterSpacing: 1.2, mb: 1, fontSize: { xs: '1.8rem', md: '2.4rem' }, textShadow: theme === 'dark' ? '0 2px 4px rgba(0,0,0,0.2)' : '0 1px 2px rgba(255,255,255,0.5)' }}>Packages</Typography>
            <Box sx={{ width: 60, height: 3, bgcolor: 'primary.main', mx: 'auto', borderRadius: 2, boxShadow: 2, mb: 2 }} />
            <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.95rem', md: '1.05rem' } }}>Choose the perfect package for your event participation</Typography>
            <Box sx={{ mx: 'auto', maxWidth: 920, mt: 1 }}>
              <Box sx={(theme) => ({
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: 3,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.56)' : 'rgba(255,255,255,0.92)',
                color: theme.palette.mode === 'dark' ? '#fff' : '#111',
                border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
              })}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.6 }}>
                  Tanzania International Medical Tourism Expo offers sponsors a unique opportunity to showcase their brands and connect with global investors, government leaders, and key industry stakeholders. Sponsorship packages are designed to provide flexibility, visibility, networking, and engagement across multiple sectors while supporting Tanzania’s economic transformation.
                </Typography>
              </Box>
            </Box>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
          ) : error ? (
            <Typography color="error" sx={{ mt: 4 }}>{error}</Typography>
          ) : packages.length === 0 ? (
            <Typography sx={{ mt: 4 }}>No packages found.</Typography>
          ) : (
            <>
              <Grid container spacing={3} justifyContent="center" alignItems="stretch">
                {packages.map((pkg, idx) => {
                  const cat = (pkg.category || 'delegate').toLowerCase();
                  const colorMap = {
                    speaker: { main: '#8e24aa', light: '#f3e5f5', border: '#ce93d8', text: '#fff', icon: <MicIcon /> },
                    sponsorship: { main: '#FFD600', light: '#fffde7', border: '#ffe082', text: '#222', icon: <LocalOfferIcon /> },
                    exhibitor: { main: '#9e9e9e', light: '#f5f5f5', border: '#cfcfcf', text: '#222', icon: <StorefrontIcon /> },
                    delegate: { main: '#800000', light: '#fbe9e7', border: '#b71c1c', text: '#fff', icon: <PeopleIcon /> },
                  };
                  const colors = colorMap[cat] || colorMap['delegate'];
                  // Decorative animal images: prefer `pkg.animal` (single URL) or
                  // `pkg.animals` (array of URLs). If none provided, use the gallery.
                  const defaultAnimalImages = [
                    'https://i.postimg.cc/KRHvGpxg/elephant.png',
                    'https://i.postimg.cc/svNDjHsS/gazelle.png',
                    'https://i.postimg.cc/2VK8k9rZ/girafe.png',
                    'https://i.postimg.cc/mt52LpBS/lion.png',
                    'https://i.postimg.cc/3dcJ8fY0/zebra.png',
                    'https://i.postimg.cc/14WyB7nZ/mount-kilimanjaro.png',
                    'https://i.postimg.cc/9wDKJpKV/monkey.png',
                    'https://i.postimg.cc/qgKGXSZ6/eagle.png',
                  ];

                  const animalImgs = Array.isArray(pkg.animals)
                    ? pkg.animals
                    : (pkg.animal ? (Array.isArray(pkg.animal) ? pkg.animal : [pkg.animal]) : defaultAnimalImages);

                  // Choose two images per card (mixing across the gallery for variety).
                  const picks = [
                    animalImgs[idx % animalImgs.length],
                    animalImgs[(idx + 1) % animalImgs.length],
                  ];

                  const baseOpacity = muiTheme.palette.mode === 'dark' ? 0.08 : 0.06;

                  // Use the first pick as a subtle full-card background, and the
                  // second pick as a smaller decorative accent on top.
                  const bgImg = picks[0];
                  const bgOpacity = muiTheme.palette.mode === 'dark' ? 0.22 : 0.16;
                  const bgFilter = muiTheme.palette.mode === 'dark' ? 'brightness(0.78) contrast(1.05) saturate(0.95)' : 'brightness(1.02) contrast(1.02) saturate(1.06)';

                  const cardKey = pkg._id || pkg.name || idx;
                  const hasLongDesc = (pkg.description || '').length > 220;
                  return (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={cardKey} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Card
                        sx={{
                          borderRadius: 6,
                          minHeight: { xs: 420, md: 480 },
                          width: '100%',
                          maxWidth: 520,
                          margin: '0 auto',
                          display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                          alignItems: 'stretch',
                          p: 0,
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: theme === 'dark'
                            ? '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)'
                            : `0 12px 40px ${colors.main}28, inset 0 1px 0 rgba(255,255,255,0.8)`,
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          background: bgMap.packages
                            ? (muiTheme.palette.mode === 'dark' ? 'rgba(24,24,40,0.72)' : 'rgba(255,255,255,0.96)')
                            : (theme === 'dark'
                              ? `linear-gradient(135deg, rgba(24,24,40,0.85) 0%, ${colors.main}12 100%)`
                              : `linear-gradient(135deg, ${colors.light}95 0%, rgba(255,255,255,0.96) 100%)`),
                          border: muiTheme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.12)'
                            : `1.5px solid ${colors.border}`,
                          transition: 'box-shadow 0.32s cubic-bezier(0.4,0,0.2,1), border-color 0.32s, transform 0.24s cubic-bezier(0.4,0,0.2,1), background 0.32s',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: theme === 'dark'
                              ? '0 24px 56px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.15)'
                              : `0 24px 64px ${colors.main}42, inset 0 1px 0 rgba(255,255,255,0.9)`,
                            borderColor: colors.main,
                            background: bgMap.packages
                              ? (muiTheme.palette.mode === 'dark' ? 'rgba(24,24,40,0.8)' : 'rgba(255,255,255,0.98)')
                              : (theme === 'dark'
                                ? `linear-gradient(135deg, rgba(24,24,40,0.9) 0%, ${colors.main}18 100%)`
                                : `linear-gradient(135deg, ${colors.light} 0%, rgba(255,255,255,0.98) 100%)`),
                          },
                        }}
                      >
                        {/* Category chip + icon */}
                        {pkg.category && (
                          <Chip
                            label={pkg.category.charAt(0).toUpperCase() + pkg.category.slice(1)}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 18,
                              right: 18,
                              fontWeight: 700,
                              fontSize: 15,
                              bgcolor: colors.main,
                              color: colors.text,
                              zIndex: 2,
                              boxShadow: 2,
                            }}
                            icon={colors.icon}
                          />
                        )}
                        {/* Accent bar */}
                        <Box sx={{ height: 10, width: '100%', bgcolor: colors.main, mb: 0.5 }} />
                        {/* Full-card background from the primary pick - Enhanced */}
                        {bgImg && typeof bgImg === 'string' && (bgImg.startsWith('http://') || bgImg.startsWith('https://')) && (
                          <>
                            <Box sx={{ position: 'absolute', inset: 0, right: 0, backgroundImage: `url(${bgImg})`, backgroundSize: 'cover', backgroundPosition: 'center right', backgroundRepeat: 'no-repeat', opacity: muiTheme.palette.mode === 'dark' ? 0.28 : 0.22, filter: bgFilter, pointerEvents: 'none', zIndex: 0, animation: 'float 6s ease-in-out infinite', '@keyframes float': { '0%, 100%': { transform: 'translateY(0px)' }, '50%': { transform: 'translateY(-8px)' } } }} />
                            {/* Artistic overlay to make animal art pop and improve contrast */}
                            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', background: muiTheme.palette.mode === 'dark' ? 'linear-gradient(135deg, rgba(0,0,0,0.2), rgba(0,0,0,0.02))' : 'linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1))', mixBlendMode: muiTheme.palette.mode === 'dark' ? 'screen' : 'overlay', opacity: 1 }} />
                          </>
                        )}

                        {/* Accent decorative image (secondary pick) - Enhanced */}
                        {picks.slice(1).map((src, i) => {
                          if (!src || typeof src !== 'string' || !(src.startsWith('http://') || src.startsWith('https://'))) return null;
                          const sx = {
                            position: 'absolute',
                            right: { xs: 8, sm: 16, md: 32 },
                            bottom: { xs: 8, sm: 16, md: 24 },
                            width: { xs: 100, sm: 140, md: 180 },
                            height: { xs: 100, sm: 140, md: 180 },
                            opacity: muiTheme.palette.mode === 'dark' ? 0.18 : 0.14,
                            pointerEvents: 'none',
                            transform: 'rotate(-12deg) scale(1)',
                            zIndex: 1,
                            objectFit: 'contain',
                            filter: muiTheme.palette.mode === 'dark' ? 'brightness(0.88) contrast(1.08)' : 'brightness(1.04) contrast(1.04) saturate(1.08)',
                            transition: 'transform 0.48s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          };
                          return <Box component="img" src={src} alt={`decorative-${i}`} sx={sx} key={`animal-${idx}-accent-${i}`} />;
                        })}
                        <CardContent sx={{
                          position: 'relative',
                          zIndex: 2,
                          flex: 1,
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                          px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 },
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mb: 1 }}>
                            <Avatar sx={{ bgcolor: colors.main, color: muiTheme.palette.mode === 'dark' ? (colors.text === '#fff' ? '#fff' : '#111') : colors.text, width: 44, height: 44, boxShadow: 2 }}>
                              {colors.icon}
                            </Avatar>
                            <Typography variant="h6" fontWeight={900} sx={{ color: muiTheme.palette.mode === 'dark' ? (colors.text === '#fff' ? '#fff' : '#e6eefc') : '#111', letterSpacing: 0.7, fontSize: { xs: 16, md: 20 }, lineHeight: 1.15 }}>{pkg.name}</Typography>
                          </Box>
                          {/* Description with read-more toggle */}
                          <Box sx={{ width: '100%', mb: 2.5 }}>
                            <Box sx={{ position: 'relative' }}>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: muiTheme.palette.mode === 'dark' ? 'rgba(230,238,252,0.95)' : '#444',
                                  fontStyle: 'italic',
                                  fontSize: { xs: 14, md: 16 },
                                  lineHeight: 1.45,
                                  maxHeight: hasLongDesc && !expandedDesc[cardKey] ? 120 : 'none',
                                  overflow: 'hidden',
                                  transition: 'max-height 0.24s ease',
                                  pr: 1
                                }}
                              >
                                {pkg.description}
                              </Typography>
                              {hasLongDesc && !expandedDesc[cardKey] && (
                                <Box sx={{ position: 'absolute', inset: '50% 0 0 0', pointerEvents: 'none', height: 48, background: `linear-gradient(180deg, rgba(255,255,255,0), ${muiTheme.palette.mode === 'dark' ? 'rgba(0,0,0,0.56)' : 'rgba(255,255,255,0.9)'})` }} />
                              )}
                            </Box>
                            {hasLongDesc && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Button aria-expanded={!!expandedDesc[cardKey]} size="small" onClick={() => setExpandedDesc(prev => ({ ...prev, [cardKey]: !prev[cardKey] }))} endIcon={expandedDesc[cardKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                  {expandedDesc[cardKey] ? 'Show less' : 'Read more'}
                                </Button>
                              </Box>
                            )}
                          </Box>
                          {pkg.includes && pkg.includes.length > 0 && (
                            <Box sx={{
                              mb: 3, width: '100%', borderRadius: 3, p: { xs: 2, md: 2.5 }, boxShadow: muiTheme.palette.mode === 'dark' ? 'inset 0 2px 8px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)' : 'inset 0 1px 3px rgba(255,255,255,0.8)',
                              ...(muiTheme.palette.mode === 'dark'
                                ? { background: `linear-gradient(135deg, rgba(${colors.main === '#8e24aa' ? '142,36,170' : colors.main === '#FFD600' ? '255,214,0' : colors.main === '#9e9e9e' ? '158,158,158' : '128,0,0'},0.08), rgba(255,255,255,0.01))`, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: `1px solid rgba(255,255,255,0.12)` }
                                : { background: `linear-gradient(135deg, ${colors.light}80, rgba(255,255,255,0.88))`, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: `1.5px solid ${colors.border}60` }
                              )
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: colors.main, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text, boxShadow: 2 }}>
                                    <CheckCircleIcon sx={{ fontSize: 22 }} />
                                  </Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: colors.main, letterSpacing: 0.3, fontSize: { xs: 15, md: 17 }, textTransform: 'uppercase' }}>What's Included</Typography>
                                </Box>
                                {pkg.includes.length > 6 && (
                                  <Button size="small" onClick={() => setShowAllIncludes(prev => ({ ...prev, [cardKey]: !prev[cardKey] }))} endIcon={showAllIncludes[cardKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ textTransform: 'none', fontWeight: 700, color: colors.main, '&:hover': { bgcolor: `${colors.main}12` } }}>
                                    {showAllIncludes[cardKey] ? 'Less' : `+${pkg.includes.length - 6}`}
                                  </Button>
                                )}
                              </Box>
                              <List dense sx={{ pl: 0, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                {(showAllIncludes[cardKey] ? pkg.includes : pkg.includes.slice(0, 6)).map((inc, i) => (
                                  <ListItem key={i} sx={{ py: 0.5, pl: 0, display: 'flex', alignItems: 'flex-start', gap: 1.2, transition: 'all 0.2s ease', '&:hover': { pl: 0.5 } }}>
                                    <Box sx={{ width: 32, minWidth: 32, height: 32, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${colors.main}22`, color: colors.main, mt: 0.2, boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)' }}>
                                      <CheckCircleIcon sx={{ fontSize: 16, fontWeight: 900 }} />
                                    </Box>
                                    <ListItemText primary={inc} primaryTypographyProps={{ fontSize: { xs: 14, md: 15.5 }, color: muiTheme.palette.mode === 'dark' ? '#e6eefc' : '#333', fontWeight: 600, lineHeight: 1.5 }} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}

                          {/* Speakers / Exhibitors inline if provided */}
                          {pkg.speakers && pkg.speakers.length > 0 && (
                            <Box sx={{ mb: 2.5, width: '100%', p: 1.5, borderRadius: 2.5, bgcolor: muiTheme.palette.mode === 'dark' ? `${colors.main}12` : `${colors.light}`, border: muiTheme.palette.mode === 'dark' ? `1px solid ${colors.main}28` : 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: colors.main, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text }}>
                                  <MicIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: colors.main, fontSize: { xs: '0.9rem', md: '1rem' }, textTransform: 'uppercase' }}>Speakers ({pkg.speakers.length})</Typography>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                                {pkg.speakers.map((s, i) => (
                                  <Chip key={i} label={s.name || s} size="small" sx={{ bgcolor: colors.main, color: colors.text, fontWeight: 800, fontSize: '0.85rem', boxShadow: 1, transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)', boxShadow: 3 } }} />
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {pkg.exhibitors && pkg.exhibitors.length > 0 && (
                            <Box sx={{ mb: 2.5, width: '100%', p: 1.5, borderRadius: 2.5, bgcolor: muiTheme.palette.mode === 'dark' ? `${colors.main}12` : `${colors.light}`, border: muiTheme.palette.mode === 'dark' ? `1px solid ${colors.main}28` : 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: colors.main, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.text }}>
                                  <StorefrontIcon sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: colors.main, fontSize: { xs: '0.9rem', md: '1rem' }, textTransform: 'uppercase' }}>Exhibitors ({pkg.exhibitors.length})</Typography>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
                                {pkg.exhibitors.map((e, i) => (
                                  <Chip key={i} label={e.name || e} size="small" sx={{ bgcolor: colors.main, color: colors.text, fontWeight: 800, fontSize: '0.85rem', boxShadow: 1, transition: 'all 0.2s ease', '&:hover': { transform: 'scale(1.05)', boxShadow: 3 } }} />
                                ))}
                              </Stack>
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mt: 'auto', pt: 3, borderTop: `1px solid ${muiTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', mb: 0.5, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Price</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 900, fontSize: { xs: 24, md: 28 }, color: colors.main, letterSpacing: 0.2 }}>{formatPrice(pkg.price)}</Typography>
                            </Box>
                          </Box>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={() => { setSelectedPackage(pkg); setBookingOpen(true); }}
                            fullWidth
                            sx={{ mt: 3, borderRadius: 2.5, fontWeight: 900, fontSize: { xs: 16, md: 18 }, alignSelf: 'stretch', px: { xs: 3, md: 5 }, py: 1.5, boxShadow: theme === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : `0 8px 24px ${colors.main}40`, background: `linear-gradient(135deg, ${colors.main}, ${colors.border})`, color: colors.text, textTransform: 'uppercase', letterSpacing: 0.5, transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)', '&:hover': { transform: 'translateY(-2px)', boxShadow: theme === 'dark' ? '0 12px 32px rgba(0,0,0,0.5)' : `0 12px 32px ${colors.main}50` }, '&:active': { transform: 'translateY(0px)' } }}
                          >
                            Book Now
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
              <Box sx={{
                mt: 6,
                py: 5,
                px: 3,
                borderRadius: 3,
                display: 'flex',
                gap: 3,
                alignItems: 'center',
                justifyContent: 'space-between',
                background: muiTheme.palette.mode === 'dark'
                  ? `linear-gradient(90deg, ${muiTheme.palette.primary.main}33, ${muiTheme.palette.secondary.main}22)`
                  : 'linear-gradient(90deg, rgba(2,6,23,0.06), rgba(255,235,59,0.04))',
                border: `1px solid ${muiTheme.palette.divider}`,
                boxShadow: muiTheme.palette.mode === 'dark' ? '0 8px 30px rgba(0,0,0,0.6)' : '0 8px 30px rgba(2,6,23,0.06)',
                backdropFilter: 'blur(6px)',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box component="img" src="https://i.postimg.cc/R3DsmxHW/main-Icon.png" alt="logo" sx={{ width: 56, height: 56, borderRadius: 1, boxShadow: 2, mr: 1, flexShrink: 0 }} />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        mb: 0.5,
                        color: '#ffffff',
                        fontWeight: 900,
                        letterSpacing: 0.4,
                        textShadow: '0 1px 0 rgba(0,0,0,0.06)'
                      }}
                    >
                      Ready to book?
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 800,
                        mb: 0.5
                      }}
                    >
                      Contact our bookings team to reserve your spot, ask questions, or request a custom package.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#FFF8E1',
                        fontWeight: 700,
                        mb: 1
                      }}
                    >
                      Reserve your spot now — limited availability. Ask about group discounts, add-on options, or express check-in.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                      <Typography
                        component="a"
                        href="mailto:bookings@example.com"
                        variant="body2"
                        sx={{
                          fontWeight: 900,
                          color: '#FFD54F',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        Email:  bookings@timte.co.tz
                      </Typography>
                      <Typography
                        component="a"
                        href="tel:+1234567890"
                        variant="body2"
                        sx={{
                          fontWeight: 900,
                          color: '#FFD54F',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        Phone: +255792898222
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setBookingOpen(true)}
                    sx={{
                      fontWeight: 900,
                      bgcolor: muiTheme.palette.primary.main,
                      color: muiTheme.palette.primary.contrastText,
                      '&:hover': { bgcolor: muiTheme.palette.primary.dark },
                      px: 3,
                      py: 1.25,
                      boxShadow: 4,
                      borderRadius: 1,
                      minWidth: 150,
                      '&:focus-visible': { outline: `3px solid ${muiTheme.palette.primary.light}`, outlineOffset: 2 },
                      '&.Mui-disabled': { opacity: 0.6 }
                    }}
                  >
                    Book Now
                  </Button>
                  <Tooltip title="Chat on WhatsApp">
                    <IconButton
                      component="a"
                      href={`https://wa.me/+255792898222`}
                      target="_blank"
                      sx={{
                        bgcolor: '#25D366',
                        color: '#fff',
                        '&:hover': { bgcolor: '#1DA851' },
                        boxShadow: 4,
                        width: 56,
                        height: 56,
                        borderRadius: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      aria-label="WhatsApp"
                    >
                      <WhatsAppIcon sx={{ fontSize: 26 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} selectedPackage={selectedPackage} />
    </PublicLayout>
  );
}

export default PackagesPage;
